import * as React from "react";
import { AlertCircle, ChevronDown } from "lucide-react";
import { Button, Card, Collapsible } from "@/components/ui";
import { DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";

interface PackageErrorStateProps {
  pkg: DesignPackageDetail;
  canEdit: boolean;
  onRetry(): Promise<void>;
  onEditRequest(): void;
  isRetrying: boolean;
}

export const PackageErrorState: React.FC<PackageErrorStateProps> = ({
  pkg,
  canEdit,
  onRetry,
  onEditRequest,
  isRetrying,
}) => (
  <Card className="rounded-2xl border-slate-200 shadow-sm" data-testid="package-error-state">
    <Card.Content className="flex flex-col items-center gap-4 py-10 text-center">
      <div className="flex size-14 items-center justify-center rounded-full bg-destructive/10">
        <AlertCircle className="size-7 text-destructive" aria-hidden="true" />
      </div>
      <h2 className="text-lg font-semibold text-slate-800">We couldn't finish this design</h2>
      {pkg.progressStatus && <p className="text-sm text-slate-600 max-w-md">{pkg.progressStatus}</p>}

      {canEdit && (
        <div className="flex gap-2">
          <Button loading={isRetrying} onClick={() => onRetry()}>
            Try again
          </Button>
          <Button variant="outline" disabled={isRetrying} onClick={onEditRequest}>
            Edit request
          </Button>
        </div>
      )}

      {canEdit && pkg.failureDetails && (
        <Collapsible className="w-full max-w-xl">
          <Collapsible.Trigger render={<Button variant="link" size="sm" />}>
            Details
            <ChevronDown aria-hidden="true" />
          </Collapsible.Trigger>
          <Collapsible.Content>
            <pre className="mt-2 max-h-60 overflow-auto rounded-md bg-muted p-3 text-left font-mono text-xs text-muted-foreground whitespace-pre-wrap">
              {pkg.failureDetails}
            </pre>
          </Collapsible.Content>
        </Collapsible>
      )}
    </Card.Content>
  </Card>
);
